import { Avatar, Box, Icon, Text, VStack } from '@squire/ui';

import { Skeleton } from '@/components/_shared/Skeleton';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { StandbyOrderInfoRowsProps } from './types';

const StandbyOrderInfoRows = ({
  layout,
  stylist,
  dateTimeLabel,
  durationLabel,
  services,
  isLoading = false,
}: StandbyOrderInfoRowsProps) => {
  const { t } = useScreenTranslations('StandbyCheckoutScreen');
  const isDesktop = layout === 'desktop';
  const nameVariant: 'lStrong' | 'mStrong' = isDesktop ? 'lStrong' : 'mStrong';
  const groupStyle = [styles.group, isDesktop && styles.groupGapDesktop];

  const stylistName = stylist?.name ?? t('anyProfessionalLabel');

  if (isLoading) {
    return (
      <VStack style={styles.container} testID="standby-order-info-rows">
        <Skeleton
          testID="standby-order-info-rows-skeleton"
          width="100%"
          height={40}
        />
        <Box style={styles.divider} />
        <Skeleton width="100%" height={40} />
        <Box style={styles.divider} />
        <Skeleton width="100%" height={40} />
      </VStack>
    );
  }

  return (
    <VStack style={styles.container} testID="standby-order-info-rows">
      <Box
        style={styles.stylistRow}
        accessible
        accessibilityLabel={t('accessibility.stylistRow', {
          name: stylistName,
        })}
        testID="standby-order-info-rows-stylist"
      >
        {stylist?.photoUrl ? (
          <Avatar
            uri={stylist.photoUrl}
            alt={stylist.name}
            shape="square"
            size="sm"
            style={styles.photo}
          />
        ) : (
          <Box style={[styles.photo, styles.photoTile]}>
            <Icon name="user" size={20} color="iconSecondary" />
          </Box>
        )}
        <VStack style={groupStyle}>
          <Text variant="m" color="textSecondary">
            {t('stylistLabel')}
          </Text>
          <Text
            variant={nameVariant}
            color="textPrimary"
            testID="standby-order-info-rows-stylist-name"
          >
            {stylistName}
          </Text>
        </VStack>
      </Box>

      <Box style={styles.divider} />

      <VStack
        style={groupStyle}
        accessible
        accessibilityLabel={t('accessibility.dateAndTimeRow', {
          date: dateTimeLabel,
          duration: durationLabel,
        })}
        testID="standby-order-info-rows-date-time"
      >
        <Text variant="m" color="textSecondary">
          {t('dateAndTimeLabel')}
        </Text>
        <Box style={styles.valueRow}>
          <Text variant="lStrong" color="textPrimary">
            {dateTimeLabel}
          </Text>
          <Text variant="lStrong" color="textPrimary">
            {durationLabel}
          </Text>
        </Box>
      </VStack>

      <Box style={styles.divider} />

      <VStack style={styles.group}>
        <Text variant="m" color="textSecondary">
          {t('servicesLabel')}
        </Text>
        <VStack style={styles.services}>
          {services.map((service) => (
            <Box
              key={service.id}
              style={styles.valueRow}
              accessible
              accessibilityLabel={t('accessibility.serviceRow', {
                name: service.name,
                price: service.priceLabel,
              })}
              testID={`standby-order-info-rows-service-${service.id}`}
            >
              <Text variant="lStrong" color="textPrimary">
                {service.name}
              </Text>
              <Text variant="lStrong" color="textPrimary">
                {service.priceLabel}
              </Text>
            </Box>
          ))}
        </VStack>
      </VStack>
    </VStack>
  );
};

StandbyOrderInfoRows.displayName = 'StandbyOrderInfoRows';
export { StandbyOrderInfoRows };
